/**
 * Registration Campaign Card Component
 *
 * Card component for managing Registration Campaign that forces users to register
 * Microsoft Authenticator on first sign-in. Prevents users from skipping setup
 * and getting blocked by Conditional Access policies later.
 *
 * Phase 3.5 - Registration Campaign
 */

import { useState, useEffect } from 'react';
import {
  makeStyles,
  tokens,
  Title3,
  Body1,
  Switch,
  Spinner,
  SpinButton,
  InfoLabel,
  Button,
  shorthands,
} from '@fluentui/react-components';
import { RoundedCard } from '../../common/RoundedCard';
import { useRegistrationCampaign } from '../../../hooks/useRegistrationCampaign';
import { Alert } from '../../common/Alert';
import type { RegistrationCampaignConfig } from '../../../types/registrationCampaign';

const useStyles = makeStyles({
  card: {
    ...shorthands.padding(tokens.spacingVerticalXL),
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap(tokens.spacingVerticalM),
  },
  settingRow: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalM),
    ...shorthands.padding(tokens.spacingVerticalS, 0),
  },
  settingLabel: {
    minWidth: 'fit-content',
  },
  spinButtonContainer: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap(tokens.spacingHorizontalS),
  },
  buttonRow: {
    display: 'flex',
    ...shorthands.gap(tokens.spacingHorizontalS),
    ...shorthands.padding(tokens.spacingVerticalM, 0),
  },
});

interface RegistrationCampaignCardProps {
  tenantId: string;
}

export const RegistrationCampaignCard: React.FC<RegistrationCampaignCardProps> = ({
  tenantId,
}) => {
  const styles = useStyles();

  // Use React Query hook for data fetching and mutations
  const {
    status,
    isLoading,
    error: fetchError,
    updateCampaign,
    isUpdating,
    updateError,
  } = useRegistrationCampaign(tenantId);

  // Local UI state
  const [success, setSuccess] = useState<string | null>(null);

  // Pending values (local edits not yet saved)
  const [pendingEnabled, setPendingEnabled] = useState(false);
  const [pendingSnoozeDays, setPendingSnoozeDays] = useState(0);

  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Sync pending values from backend data when it loads
  useEffect(() => {
    if (status) {
      setPendingEnabled(status.enabled);
      setPendingSnoozeDays(status.snooze_days);
      setHasUnsavedChanges(false);
    }
  }, [status]);

  // Auto-dismiss success messages after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const handleToggleEnabled = (checked: boolean) => {
    setPendingEnabled(checked);
    setHasUnsavedChanges(true);
  };

  const handleSnoozeDaysChange = (newValue: number) => {
    setPendingSnoozeDays(newValue);
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    const config: RegistrationCampaignConfig = {
      enabled: pendingEnabled,
      snooze_days: pendingEnabled ? pendingSnoozeDays : 0,
    };

    try {
      const updated = await updateCampaign(config);
      setHasUnsavedChanges(false);
      setSuccess(
        updated.enabled
          ? 'Registration Campaign enabled successfully'
          : 'Registration Campaign disabled successfully'
      );
    } catch {
      // Error is captured in hook's updateError
    }
  };

  const handleCancel = () => {
    // Revert pending values to confirmed values from hook
    if (status) {
      setPendingEnabled(status.enabled);
      setPendingSnoozeDays(status.snooze_days);
    }
    setHasUnsavedChanges(false);
  };

  if (isLoading) {
    return (
      <RoundedCard className={styles.card}>
        <div className={styles.cardContent}>
          <Title3>Registration Campaign</Title3>
          <Spinner label="Loading Registration Campaign configuration..." />
        </div>
      </RoundedCard>
    );
  }

  // Combine fetch and update errors
  const error = fetchError?.message ?? updateError?.message ?? null;

  return (
    <RoundedCard className={styles.card}>
      <div className={styles.cardContent}>
        <Title3>Registration Campaign</Title3>
        <Body1>
          Force users to register Microsoft Authenticator on first sign-in. Prevents users from
          skipping setup and getting blocked by Conditional Access policies later.
        </Body1>

        {error && <Alert intent="error">{error}</Alert>}
        {success && <Alert intent="success">{success}</Alert>}

        <div className={styles.settingRow}>
          <InfoLabel
            className={styles.settingLabel}
            info="When enabled, users must register Microsoft Authenticator on first sign-in"
          >
            Enable Registration Campaign
          </InfoLabel>
          <Switch
            checked={pendingEnabled}
            onChange={(_, data) => handleToggleEnabled(data.checked)}
            disabled={isUpdating}
            aria-label="Enable Registration Campaign"
          />
        </div>

        {pendingEnabled && (
          <div className={styles.settingRow}>
            <InfoLabel
              className={styles.settingLabel}
              info="Number of days users can postpone Authenticator setup (0 = immediate enforcement)"
            >
              Snooze Days
            </InfoLabel>
            <div className={styles.spinButtonContainer}>
              <SpinButton
                value={pendingSnoozeDays}
                onChange={(_, data) => {
                  if (data.value !== undefined && data.value !== null) {
                    handleSnoozeDaysChange(data.value);
                  }
                }}
                min={0}
                max={14}
                step={1}
                disabled={isUpdating}
                aria-label="Snooze days (0-14)"
                style={{ width: '100px' }}
              />
              <Body1>days</Body1>
            </div>
          </div>
        )}

        {hasUnsavedChanges && (
          <div className={styles.buttonRow}>
            <Button appearance="primary" onClick={handleSave} disabled={isUpdating}>
              {isUpdating ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={handleCancel} disabled={isUpdating}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    </RoundedCard>
  );
};
